import { BaseController } from "./baseController";
import { type CIReferral } from "components/CriticalIncident/interfaces";
import { isMockServerRunning } from "utils/isMockServerRunning";
import { getIncidentReportStatus } from "./getIncidentReportStatus";
import { CASE_ACTIVITY_IDS } from "./criticalIncidentCaseActivityIds";

export class CriticalIncidentController extends BaseController {
    private readonly isMockMode: boolean;

    static async resolveMockApiHost(): Promise<string> {
        return (await isMockServerRunning()) ? 'http://localhost:8009/api' : '/server/api';
    }

    constructor(apiHost: string, version = 'v1', isMockMode = false, isFeatureEnabled = true) {
        super(apiHost, version, isFeatureEnabled);
        this.isMockMode = isMockMode;
    }

    protected async resolveApiHost(): Promise<string> {
        return this.isMockMode ? CriticalIncidentController.resolveMockApiHost() : this.apiHost;
    }

    // Referrals
    async getCriticalIncidentReferral(id: string, version = this.version): Promise<CIReferral> {
        const referral = await this.get<CIReferral, void>(await this.getEndpointAsync(`referrals/${id}`, version));
        const referrals = await this.getCriticalIncidentReferrals(version);
        const matched = referrals.find(r => r.referralId === id);
        return {
            ...referral,
            // @ts-ignore
            caseId: matched?.caseId,
            // @ts-ignore
            activityId: matched?.activityId,
            // @ts-ignore
            incidentReportStatus: matched?.incidentReportStatus,
        };
    }

    async getCriticalIncidentReferralByExternalId(externalId: string, version = this.version): Promise<CIReferral> {
        return this.get<CIReferral, { id: string }>(await this.getEndpointAsync(`referrals/external`, version), { id: externalId });
    }

    async getCriticalIncidentReferrals(version = this.version): Promise<CIReferral[]> {
        const referrals = await this.get<CIReferral[], void>(await this.getEndpointAsync(`referrals`, version));
        return Promise.all((referrals ?? []).map(async (referral, index) => {
            const { caseId, activityId } = CASE_ACTIVITY_IDS[index] ?? {};
            return {
                ...referral,
                // @ts-ignore
                caseId,
                activityId,
                incidentReportStatus: caseId != null && activityId != null ? await getIncidentReportStatus(caseId, activityId) : undefined,
            };
        }));
    }

    async acceptCriticalIncidentReferral(id: string, version = this.version): Promise<CIReferral> {
        return this.post<CIReferral, void>(await this.getEndpointAsync(`referrals/${id}/accept`, version));
    }

    async declineCriticalIncidentReferral(id: string, reason: string, version = this.version): Promise<CIReferral> {
        return this.post<CIReferral, { reason: string }>(await this.getEndpointAsync(`referrals/${id}/decline`, version), { reason });
    }
}
